
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  const cookieTypes = [
    {
      name: 'Necessary Cookies',
      purpose: 'Essential for website functionality',
      retention: 'Session or up to 1 year',
      examples: [
        'trbe-cookie-consent - Stores your cookie preferences',
        'session-token - Maintains your session',
        'csrf-token - Security protection'
      ]
    },
    {
      name: 'Functional Cookies',
      purpose: 'Enhance user experience and personalization',
      retention: 'Up to 2 years',
      examples: [
        'language-preference - Remembers your language choice',
        'region-setting - Stores your location preference',
        'accessibility-settings - Saves accessibility preferences'
      ]
    },
    {
      name: 'Analytics Cookies',
      purpose: 'Help us understand website usage',
      retention: 'Up to 26 months',
      examples: [
        '_ga - Google Analytics identifier',
        '_gid - Google Analytics session identifier',
        '_gat - Google Analytics throttling'
      ]
    },
    {
      name: 'Marketing Cookies',
      purpose: 'Deliver relevant advertisements',
      retention: 'Up to 12 months',
      examples: [
        'fbp - Facebook Pixel',
        'li_sugr - LinkedIn Insight Tag',
        'ads-preferences - Advertisement targeting'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Learn about how we use cookies and similar technologies on our website
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <div className="prose max-w-none">
              <div className="space-y-8 text-neutral-700">
                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    What Are Cookies?
                  </h2>
                  <p className="mb-4">
                    Cookies are small text files that are stored on your device when you visit a website. 
                    They help websites remember information about your visit, which can make your next 
                    visit easier and the site more useful to you.
                  </p>
                  <p>
                    We use cookies and similar technologies to improve your browsing experience, 
                    provide personalized content, analyze our traffic, and support our marketing efforts.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                    Types of Cookies We Use
                  </h2>
                  <div className="space-y-6">
                    {cookieTypes.map((type, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                          {type.name}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <strong className="text-neutral-900">Purpose:</strong>
                            <p className="text-neutral-600">{type.purpose}</p>
                          </div>
                          <div>
                            <strong className="text-neutral-900">Retention:</strong>
                            <p className="text-neutral-600">{type.retention}</p>
                          </div>
                        </div>
                        <div>
                          <strong className="text-neutral-900">Examples:</strong>
                          <ul className="list-disc list-inside text-neutral-600 mt-2 space-y-1">
                            {type.examples.map((example, idx) => (
                              <li key={idx}>{example}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Third-Party Services
                  </h2>
                  <p className="mb-4">
                    We may use third-party services that set cookies on your device. These include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600">
                    <li><strong>Google Analytics:</strong> Web analytics service to understand website usage</li>
                    <li><strong>LinkedIn Insight Tag:</strong> Analytics and conversion tracking for LinkedIn</li>
                    <li><strong>Facebook Pixel:</strong> Analytics and advertising optimization</li>
                    <li><strong>Netlify:</strong> Website hosting and performance analytics</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Managing Your Cookie Preferences
                  </h2>
                  <p className="mb-4">
                    You have several options to control cookies:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600 mb-4">
                    <li><strong>Cookie Banner:</strong> Accept or reject cookies when you first visit our site</li>
                    <li><strong>Cookie Settings:</strong> Use the cookie preferences button to customize your choices</li>
                    <li><strong>Browser Settings:</strong> Configure your browser to block or delete cookies</li>
                    <li><strong>Opt-out Links:</strong> Use third-party opt-out tools for advertising cookies</li>
                  </ul>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800">
                      <strong>Note:</strong> Blocking necessary cookies may affect website functionality. 
                      You can withdraw consent at any time, but this may limit your access to certain features.
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Data Retention
                  </h2>
                  <p className="mb-4">
                    We retain cookie data for different periods depending on the type:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600">
                    <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                    <li><strong>Functional Cookies:</strong> Up to 2 years or until you clear them</li>
                    <li><strong>Analytics Cookies:</strong> Up to 26 months (Google Analytics default)</li>
                    <li><strong>Marketing Cookies:</strong> Up to 12 months or as specified by third parties</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Your Rights
                  </h2>
                  <p className="mb-4">
                    Under GDPR and other privacy laws, you have the right to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-600">
                    <li>Be informed about our cookie usage (this policy)</li>
                    <li>Give or withdraw consent for non-essential cookies</li>
                    <li>Access data collected through cookies</li>
                    <li>Request deletion of cookie data</li>
                    <li>Object to processing for direct marketing purposes</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Updates to This Policy
                  </h2>
                  <p>
                    We may update this Cookie Policy periodically to reflect changes in our practices 
                    or applicable laws. We will notify you of any material changes by posting the 
                    updated policy on our website and updating the "Last Updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Contact Us
                  </h2>
                  <p className="mb-4">
                    If you have questions about this Cookie Policy or our privacy practices:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p><strong>Trbe UG (haftungsbeschränkt)</strong></p>
                    <p>Email: <a href="mailto:privacy@trbe.ai" className="text-brand-orange hover:underline">privacy@trbe.ai</a></p>
                    <p>Address: Rheinstr. 46, 12161 Berlin, Germany</p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-500">
                    <strong>Last Updated:</strong> January 2025
                  </p>
                  <div className="mt-4 flex space-x-4">
                    <Link 
                      to="/privacy" 
                      className="text-brand-orange hover:underline text-sm"
                    >
                      Privacy Policy
                    </Link>
                    <Link 
                      to="/imprint" 
                      className="text-brand-orange hover:underline text-sm"
                    >
                      Imprint
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
